import {hpcAttrs} from '@github-ui/hpc'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IconButton, Label, Link, RelativeTime} from '@primer/react'
import {PencilIcon} from '@primer/octicons-react'
import {useRef, useState} from 'react'
import {clsx} from 'clsx'
import {DescriptionEditor} from './DescriptionEditor'
import {useDescriptionData} from '../../page-data/loaders/use-description-data'
import {useOverviewRouteData} from './data/use-overview-route-data'
import type {SafeHTMLString} from '@github-ui/safe-html'
import styles from './Description.module.css'

import {useLayoutRouteData} from '../../page-data/loaders/use-layout-route-data'
import {useLayoutCanEditTitle} from '../../stores/layout-selectors'
import {PullRequestHeaderAuthors} from '../PullRequestHeaderAuthors'

/** PR description section on the Overview tab */
export function Description() {
  const {
    description: initialDescriptionHtml,
    descriptionText: initialDescriptionText,
    editInformation,
  } = useOverviewRouteData().data
  const {
    pullRequest: {author, attributedUser, createdTime},
  } = useLayoutRouteData()
  // Editing the title has the same permissions as editing the description
  const canEditDescription = useLayoutCanEditTitle()

  const {data: descriptionData} = useDescriptionData({
    initialBodyHtml: initialDescriptionHtml ?? undefined,
    initialBodyText: initialDescriptionText ?? undefined,
    initialEditInformation: editInformation,
  })
  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
  const description = descriptionData?.bodyHtml ? (descriptionData.bodyHtml as SafeHTMLString) : null
  const body = descriptionData?.body ?? ''
  const hasEdits =
    descriptionData?.editInformation?.viewerCanReadUserContentEdits &&
    !!descriptionData.editInformation.lastUserContentEdit

  const [isEditing, setIsEditing] = useState(false)
  const editButtonRef = useRef<HTMLButtonElement>(null)

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const copilot = isCopilot(author.login)
  const isAgent = author.isAgent ?? copilot
  const showCoAttribution = isFeatureEnabled('pull_request_copilot_attribution_header') && isAgent && !!attributedUser
  const appSlug = author.slug || (copilot ? 'copilot-swe-agent' : author.login)
  const profileUrl = isAgent ? `/apps/${appSlug}` : `/${author.login}`
  const hovercardAttributes = isAgent
    ? getHovercardAttributesForType('copilot', {botSlug: appSlug})
    : getHovercardAttributesForType('user', {login: author.login})
  const avatar = copilot ? (
    <CopilotAvatar size="medium" isDecorative className={styles.avatarImage} />
  ) : author.avatarUrl ? (
    <GitHubAvatar src={author.avatarUrl} size={24} className={styles.avatarImage} />
  ) : null

  return (
    <section className={styles.container} aria-label="Description" {...hpcAttrs()}>
      <div className={styles.header}>
        <div className={styles.author}>
          {showCoAttribution && attributedUser ? (
            <PullRequestHeaderAuthors
              agent={author}
              attributedUser={attributedUser}
              showAvatars
              className={styles.coauthors}
            />
          ) : (
            <>
              {avatar ? (
                <Link
                  href={profileUrl}
                  {...hovercardAttributes}
                  aria-label={`${author.login}'s profile`}
                  className={styles.avatar}
                >
                  {avatar}
                </Link>
              ) : null}
              <Link href={profileUrl} {...hovercardAttributes} className={styles.authorName}>
                {author.login}
              </Link>
            </>
          )}
          {isAgent && <Label variant="secondary">AI</Label>}
        </div>
        <span className={styles.opened}>opened {createdTime && <RelativeTime date={new Date(createdTime)} />}</span>
        <div className={styles.editMetadata}>
          <div className={styles.editHistory}>{hasEdits && <span>Edited</span>}</div>
          {canEditDescription && (
            <IconButton
              ref={editButtonRef}
              icon={PencilIcon}
              aria-label="Edit description"
              variant="invisible"
              size="small"
              className={clsx(styles.editButton, isEditing && 'v-hidden')}
              onClick={() => setIsEditing(true)}
            />
          )}
        </div>
      </div>
      <div className={styles.body}>
        <DescriptionEditor
          bodyHtml={description}
          body={body}
          isEditing={isEditing}
          onEditingChange={setIsEditing}
          returnFocusRef={editButtonRef}
        />
      </div>
    </section>
  )
}
